import React, { useCallback, useEffect, useState } from 'react';
import {
  SalvButton,
  SalvProgressCircle,
  SalvTable,
  SalvTablePaginationType,
  SalvTooltipWrapper,
  Size,
} from '@salvapp/ui-components';
import { InternalApiAlertType, InternalApiCaseAlert, InternalApiSortDirection } from '../../../api-internal';
import { useDetachCaseAlerts, useQueryCaseAlertsPage } from './caseAlertQueryActions';
import { formatDate } from '../../../common/datetime/FormattedDateTime';
import { useDispatch } from 'react-redux';
import { showConfirmationModal } from '../../../confirmation-modal/confirmationModalReducer';
import { showFeedback } from '../../../feedback/feedbackReducer';
import Has from '../../../auth/Has';
import { CAN_MODIFY_CASE } from '../../../auth/permissions';
import AttachCaseAlertsModal from './AttachCaseAlertsModal';
import DataLoadingErrorFallback from '../../../common/errors/DataLoadingErrorFallback';
import { ColumnSort, createColumnHelper, PaginationState, SortingState } from '@tanstack/react-table';
import { Updater } from '@tanstack/table-core/src/types';
import { getOrderByAndDirection } from '../../../common/sort';
import { useNavigate } from 'react-router';
import {
  getBridgeAlertPath,
  getManualAlertPath,
  getMonitoringAlertPath,
  getScreeningAlertPath,
} from '../../../navigation/helper';
import { useAlertStatuses } from '../../../screening/alert-status/alertStatusesHelper';
import { CaseAlertDetailsCell } from '../../../alerts/filters/helper';
import '../../../alerts/filters/AlertFilters.scss';

interface CaseAlertsProps {
  handle: string;
}

const PAGE_SIZE = 5;
const DEFAULT_ORDER_BY = 'createdAt';
const DEFAULT_DIRECTION = InternalApiSortDirection.DESC;

const alertTypeLabels: Record<InternalApiAlertType, string> = {
  [InternalApiAlertType.SCREENING]: 'Screening',
  [InternalApiAlertType.MONITORING]: 'Monitoring',
  [InternalApiAlertType.BRIDGE]: 'Bridge',
  [InternalApiAlertType.MANUAL]: 'Manual',
};

const CaseAlerts = ({ handle }: CaseAlertsProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [, statuses] = useAlertStatuses();
  const [page, setPage] = useState(0);
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [direction, setDirection] = useState(DEFAULT_DIRECTION);

  const columnSort: ColumnSort = { id: orderBy, desc: direction === InternalApiSortDirection.DESC };

  const {
    data: alertsPage,
    isPending,
    isFetching,
    isError,
    error,
  } = useQueryCaseAlertsPage({
    handle,
    page,
    size: PAGE_SIZE,
    orderBy,
    direction,
  });

  useEffect(() => {
    setSelectedRows([]);
  }, [alertsPage]);

  const detachCaseAlerts = useDetachCaseAlerts();

  const [showAttachModal, setShowAttachModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState<InternalApiCaseAlert[]>([]);

  const setPagination = useCallback((newPagination: PaginationState) => {
    setPage(newPagination.pageIndex);
  }, []);

  const setSorting = useCallback(
    (updaterOrValue: Updater<SortingState>) => {
      const { orderBy: newOrderBy = DEFAULT_ORDER_BY, direction: newDirection = DEFAULT_DIRECTION } =
        getOrderByAndDirection(updaterOrValue, [columnSort]);

      setOrderBy(newOrderBy);
      setDirection(newDirection);
      setPage(0);
    },
    [columnSort]
  );

  const handleDetach = async () => {
    const count = selectedRows.length;
    const isConfirmed = await dispatch(
      showConfirmationModal({
        title: 'Detach alerts?',
        description: `${count} alert${count > 1 ? 's' : ''} will be detached from this case.`,
        confirmationText: 'Yes, detach',
      })
    );

    if (!isConfirmed) return;

    try {
      await detachCaseAlerts.mutateAsync({
        handle,
        alerts: selectedRows.map((row) => ({ alertType: row.alert.type, alertId: row.alert.id })),
      });
      dispatch(showFeedback({ message: 'Alerts detached successfully', success: true }));
      setSelectedRows([]);
    } catch {
      dispatch(showFeedback({ message: 'Failed to detach alerts', success: false }));
    }
  };

  const columnHelper = createColumnHelper<InternalApiCaseAlert>();

  const getColumns = useCallback(
    () => [
      columnHelper.accessor((row) => row.alert.id, {
        id: 'alertId',
        header: 'Alert ID',
        enableSorting: true,
      }),
      columnHelper.display({
        id: 'reason',
        header: 'Details',
        cell: ({ row }) => <CaseAlertDetailsCell caseAlert={row.original} />,
      }),
      columnHelper.accessor((row) => row.alert.type, {
        id: 'alertType',
        header: 'Alert Type',
        enableSorting: true,
        cell: ({ getValue }) => {
          return alertTypeLabels[getValue()];
        },
      }),
      columnHelper.display({
        id: 'status',
        header: 'Status',
        cell: ({ row }) => statuses.find((s) => s.id === row.original.alert.statusId)?.textName ?? '',
      }),
      columnHelper.accessor('createdAt', {
        header: 'Attached',
        cell: ({ getValue, row }) => (
          <SalvTooltipWrapper
            tooltip={`${row.original.createdBy.name}, ${formatDate(getValue(), true)}`}
            placement='top'
          >
            {formatDate(getValue())}
          </SalvTooltipWrapper>
        ),
      }),
    ],
    [statuses]
  );

  if (isError) {
    return <DataLoadingErrorFallback message='Could not load Case Alerts' error={error} />;
  }

  return (
    <>
      {isPending ? (
        <SalvProgressCircle ariaLabel={'Loading Cases'} />
      ) : (
        <SalvTable<InternalApiCaseAlert>
          title='Alerts'
          columns={getColumns()}
          data={alertsPage.content}
          selectedRows={alertsPage.content.length ? selectedRows : undefined}
          setSelectedRows={setSelectedRows}
          isLoading={isFetching}
          emptyMessage='No Alerts attached to this case'
          hover
          expandable
          pagination={{
            type: SalvTablePaginationType.PAGINATION,
            total: alertsPage.total,
            state: { pageIndex: page, pageSize: PAGE_SIZE },
            setPagination,
          }}
          sorting={{
            state: [columnSort],
            setSorting,
          }}
          rowClick={({ original }) => {
            switch (original.alert.type) {
              case InternalApiAlertType.SCREENING:
                return navigate(getScreeningAlertPath(original.alert.id));
              case InternalApiAlertType.MONITORING:
                return navigate(getMonitoringAlertPath(original.alert.id));
              case InternalApiAlertType.BRIDGE:
                return navigate(getBridgeAlertPath(original.alert.id));
              case InternalApiAlertType.MANUAL:
                return navigate(getManualAlertPath(original.alert.id));
            }
          }}
          actions={
            <Has allPermissions={[CAN_MODIFY_CASE]}>
              <div style={{ display: 'flex', gap: '8px' }}>
                {selectedRows.length > 0 && (
                  <SalvButton
                    variant='muted'
                    size={Size.SMALL}
                    onClick={handleDetach}
                    label='Detach Selected'
                    disabled={detachCaseAlerts.isPending}
                  />
                )}
                <SalvButton
                  variant='secondary'
                  size={Size.SMALL}
                  onClick={() => setShowAttachModal(true)}
                  label='Attach Alerts'
                />
              </div>
            </Has>
          }
        />
      )}
      <AttachCaseAlertsModal handle={handle} show={showAttachModal} onClose={() => setShowAttachModal(false)} />
    </>
  );
};

export default CaseAlerts;
